import Image from '@base/Image';
import styled from '@emotion/styled';
import { useIsMobile } from '@stores/AppCommonData';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import { useEffect, useState } from 'react';
import useCertificationCourseData from './useCertificationCourseData';

const Container = styled.div`
  display: grid;
  width: 100%;
  row-gap: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    row-gap: ${Spacings.SPACING_4B};
  }
`;

const Title = styled(Typography)``;

const FacultiesContainer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_6B};
  flex-direction: column;
  margin-top: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_4B};
    overflow-x: hidden;
  }
`;

const FacultyList = styled.div`
  width: 768px;
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    flex-wrap: nowrap;
    overflow-x: auto;
    white-space: nowrap;
    gap: ${Spacings.SPACING_4B};
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
`;
const FacultyContainer = styled.div`
  padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_4B};
  width: 372px;
  min-height: 342px;
  background: var(--color-base-1);
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  flex-direction: column;
  margin-right: ${({ $margin }) => {
    return $margin || '0px';
  }};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_4B};
    width: 300px;
    min-height: 336px;
    margin: ${Spacings.SPACING_0B};
  }
`;
const FacultyWrapper = styled.div`
  width: 188px;
  height: 196px;
  border-radius: ${Spacings.SPACING_2B};
  position: relative;
  img {
    border-radius: ${Spacings.SPACING_2B};
    object-fit: cover;
  }
  ${mediaQuery.uptoMobile} {
    width: 116px;
    height: 120px;
    border-radius: ${Spacings.SPACING_1B};
    img {
      border-radius: ${Spacings.SPACING_1B};
    }
  }
`;

const Name = styled(Typography)`
  white-space: normal;
  margin-top: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.LARGE};
    line-height: ${Spacings.SPACING_6B};
  }
`;

const Organization = styled(Typography)`
  white-space: normal;
  margin: ${Spacings.SPACING_1B} ${Spacings.SPACING_0B};
`;

const Declaration = styled(Typography)`
  margin-top: ${Spacings.SPACING_4B};
  max-width: 330px;
  ${mediaQuery.uptoMobile} {
    row-gap: ${Spacings.SPACING_4B};
    white-space: break-spaces;
    min-width: 268px;
  }
`;
const ComponyLogo = styled.div`
  position: relative;
  width: 82px;
  min-height: 28px;
`;

const LinkedInLink = styled.div`
  margin-top: ${Spacings.SPACING_4B};
  cursor: pointer;
  font-weight: ${Fonts.LIGHT};
  font-size: ${Fonts.SMALL};
  line-height: ${Spacings.SPACING_5B};
  color: var(--color-i-blue);
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;
const TagWrapper = styled.div`
  position: relative;
  display: none;
  ${mediaQuery.uptoMobile} {
    width: 30px;
    height: 30px;
    margin-top: -30px;
    cursor: pointer;
    display: block;
  }
`;

const FacultyComponent = ({ faculty = {}, margin }) => {
  const handleLinkedInClick = () => {
    window.open(faculty.facultyLinkedInProfile, '_blank');
  };
  return (
    <FacultyContainer $margin={margin}>
      <FacultyWrapper>
        <Image src={faculty.facultyprofileImage} layout="fill" alt="faculty" />
      </FacultyWrapper>
      <TagWrapper onClick={handleLinkedInClick}>
        <Image
          src="certificationCourses/linkedIn-icon.svg"
          layout="fill"
          alt="certificate"
        />
      </TagWrapper>
      <Name variant="h4">{faculty.facultyName}</Name>
      <Organization variant="p2" color="var(--color-text-secondary)">
        {faculty.facultyDesignation}
      </Organization>
      {faculty?.facultyOrganisationLogo && (
        <ComponyLogo>
          <Image
            src={faculty.facultyOrganisationLogo}
            layout="fill"
            alt="faculty"
          />
        </ComponyLogo>
      )}
      <Declaration variant="p2">{faculty.facultyAchievements}</Declaration>
      <LinkedInLink onClick={handleLinkedInClick}>LinkedIn</LinkedInLink>
    </FacultyContainer>
  );
};

const Faculties = ({ faculties = {} }) => {
  const { programmeFaculties = [] } = faculties;
  const isMobile = useIsMobile();
  const [showCarrousel, setShowCarousel] = useState(false);
  const dataLength = programmeFaculties.length;

  useEffect(() => {
    if (isMobile) {
      return setShowCarousel(true);
    }
    if (!isMobile && dataLength > 4) {
      return setShowCarousel(true);
    }
    return setShowCarousel(false);
  }, [isMobile, dataLength]);

  return (
    <FacultiesContainer>
      {showCarrousel && (
        <ShowCaseContainer>
          <ShowCase
            itemsPerSlide={2}
            title={
              <Title variant="h3">{faculties?.programmeFacultiesTitle}</Title>
            }
          >
            {programmeFaculties.map((mentor) => {
              return (
                <FacultyComponent
                  key={mentor.uid}
                  faculty={mentor}
                  margin="8px"
                />
              );
            })}
          </ShowCase>
        </ShowCaseContainer>
      )}
      {!showCarrousel && (
        <Title variant="h3">{faculties?.programmeFacultiesTitle}</Title>
      )}
      {!showCarrousel && (
        <FacultyList>
          {programmeFaculties.map((faculty) => {
            return <FacultyComponent key={faculty.uid} faculty={faculty} />;
          })}
        </FacultyList>
      )}
    </FacultiesContainer>
  );
};

const MentorsContainer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_6B};
  flex-direction: column;
  margin-top: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_4B};
    overflow-x: hidden;
    margin-top: ${Spacings.SPACING_2B};
  }
`;
const MentorList = styled.div`
  width: 768px;
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    flex-wrap: nowrap;
    overflow-x: auto;
    white-space: nowrap;
    gap: ${Spacings.SPACING_4B};
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
`;
const MentorContainer = styled.div`
  padding: ${Spacings.SPACING_6B};
  gap: ${Spacings.SPACING_4B};
  width: 364px;
  background: var(--color-base-1);
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  flex-direction: row;
  margin-right: ${({ $margin }) => {
    return $margin || '0px';
  }};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_3B};
    width: 294px;
    min-height: 144px;
    margin: ${Spacings.SPACING_0B};
  }
`;
const MentorWrapper = styled.div`
  width: 120px;
  height: 160px;
  border-radius: ${Spacings.SPACING_2B};
  position: relative;
  img {
    border-radius: ${Spacings.SPACING_2B};
    object-fit: cover;
  }
  ${mediaQuery.uptoMobile} {
    width: 116px;
    height: 120px;
    border-radius: ${Spacings.SPACING_1B};
    img {
      border-radius: ${Spacings.SPACING_1B};
    }
  }
`;
const MentorName = styled(Typography)`
  white-space: normal;
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.LARGE};
    line-height: ${Spacings.SPACING_6B};
  }
`;
const LeftContainer = styled.div``;
const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubContainer = styled.div`
  flex: 1;
`;

const MentorComponyLogo = styled.div`
  position: relative;
  width: 82px;
  min-height: ${Spacings.SPACING_7B};
  margin-top: ${Spacings.SPACING_4B};
`;

const MentorTitle = styled(Typography)`
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.LARGE};
    line-height: ${Spacings.SPACING_6B};
  }
`;

const MentorComponent = ({ mentor = {}, margin }) => {
  const handleLinkedInClick = () => {
    window.open(mentor.memberLinkedInProfile, '_blank');
  };
  return (
    <MentorContainer $margin={margin}>
      <LeftContainer>
        <MentorWrapper>
          <Image src={mentor.memberProfileImage} layout="fill" alt="mentor" />
        </MentorWrapper>
        <TagWrapper onClick={handleLinkedInClick}>
          <Image
            src="certificationCourses/linkedIn-icon.svg"
            layout="fill"
            alt="linkedIn-icon"
          />
        </TagWrapper>
      </LeftContainer>
      <RightContainer>
        <SubContainer>
          <MentorName variant="h4">{mentor.memberName}</MentorName>
          <Organization variant="p2" color="var(--color-text-secondary)">
            {mentor.memberDesignation}
          </Organization>
          {mentor?.memberOrganisationlogo && (
            <MentorComponyLogo>
              <Image
                src={mentor.memberOrganisationlogo}
                layout="fill"
                alt="mentor"
              />
            </MentorComponyLogo>
          )}
        </SubContainer>
        {mentor.memberLinkedInProfile && (
          <LinkedInLink onClick={handleLinkedInClick}>LinkedIn</LinkedInLink>
        )}
      </RightContainer>
    </MentorContainer>
  );
};
export const ShowCaseContainer = styled.div`
  width: 768px;
  overflow-x: hidden;
  overflow-y: hidden;
  ${mediaQuery.uptoMobile} {
    width: 360px;
  }
`;

const Mentors = ({ faculties = {} }) => {
  const { programmeLiveSessionMembers = [] } = faculties;
  const isMobile = useIsMobile();
  const [showCarrousel, setShowCarousel] = useState(false);
  const dataLength = programmeLiveSessionMembers.length;

  useEffect(() => {
    if (isMobile) {
      return setShowCarousel(true);
    }
    if (!isMobile && dataLength > 4) {
      return setShowCarousel(true);
    }
    return setShowCarousel(false);
  }, [isMobile, dataLength]);

  return (
    <MentorsContainer>
      {showCarrousel && (
        <ShowCaseContainer>
          <ShowCase
            itemsPerSlide={2}
            title={faculties?.programmeLiveSessionTitle}
          >
            {programmeLiveSessionMembers.map((mentor) => {
              return (
                <MentorComponent
                  key={mentor.uid}
                  mentor={mentor}
                  margin="8px"
                />
              );
            })}
          </ShowCase>
        </ShowCaseContainer>
      )}
      {!showCarrousel && (
        <MentorTitle variant="h4">
          {'Live sessions with industry mentors' ||
            faculties?.programmeLiveSessionTitle}
        </MentorTitle>
      )}
      {!showCarrousel && (
        <MentorList>
          {programmeLiveSessionMembers.map((mentor) => {
            return <MentorComponent key={mentor.uid} mentor={mentor} />;
          })}
        </MentorList>
      )}
    </MentorsContainer>
  );
};

const Faculty = ({ goalUID }) => {
  const { sections } = useCertificationCourseData(goalUID);
  const faculties = sections?.faculties ?? {};

  return (
    <Container>
      <Faculties faculties={faculties} />
      <Mentors faculties={faculties} />
    </Container>
  );
};

export default Faculty;
